import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { AppHeader } from 'layout/AppHeader';
import { Sidebar } from 'layout/Sidebar';
import { readSidebarCollapsed, writeSidebarCollapsed } from 'layout/sidebarStore';
import { useIsDesktop } from 'layout/useMediaQuery';
import { useBodyScrollLock, useFocusTrap } from 'layout/useFocusTrap';

export function DashboardLayout() {
  const { t, locale, setLocale, dir } = useI18n();
  const { admin, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useFocusTrap(!desktop && mobileOpen, drawerRef, { onEscape: closeMobile });
  useBodyScrollLock(!desktop && mobileOpen);

  useEffect(() => {
    if (desktop) {
      setMobileOpen(false);
    }
  }, [desktop]);

  useEffect(() => {
    writeSidebarCollapsed(collapsed);
  }, [collapsed]);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        if (desktop) {
          setCollapsed((value) => !value);
        } else {
          setMobileOpen((value) => !value);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [desktop]);

  const onLogout = async () => {
    try {
      await logout();
    } finally {
      navigate(paths.login, { replace: true });
    }
  };

  const onToggleSidebar = () => {
    if (desktop) {
      setCollapsed((value) => !value);
      return;
    }
    setMobileOpen((value) => !value);
  };

  const shellClass = [
    'shell',
    collapsed && desktop ? 'is-collapsed' : '',
    dir === 'rtl' ? 'is-rtl' : 'is-ltr',
  ].filter(Boolean).join(' ');

  return (
    <div className={shellClass} dir={dir} data-testid="app-shell">
      <a className="skip-link" href="#main-content">{t('layout.skipToContent')}</a>
      <div
        className={mobileOpen && !desktop ? 'drawer-backdrop is-open' : 'drawer-backdrop'}
        onClick={closeMobile}
        aria-hidden={!mobileOpen || desktop}
      />
      <div ref={drawerRef} className="sidebar-slot">
        <Sidebar
          t={t}
          dir={dir}
          hasPermission={hasPermission}
          collapsed={desktop ? collapsed : false}
          desktop={desktop}
          mobileOpen={mobileOpen}
          onNavigate={closeMobile}
          onClose={closeMobile}
          closeLabel={t('actions.close')}
          brand={t('appName')}
          menuLabel={t('layout.menu')}
        />
      </div>
      <div className="shell-main">
        <AppHeader
          t={t}
          locale={locale}
          setLocale={setLocale}
          dir={dir}
          desktop={desktop}
          collapsed={collapsed}
          onToggleSidebar={onToggleSidebar}
          admin={admin}
          onLogout={onLogout}
          canViewNotifications={hasPermission(PERMISSIONS.notificationsView) || hasPermission(PERMISSIONS.notificationsManage)}
        />
        <main id="main-content" className="content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
